import React, {
    Ref,
    useEffect, useRef, useState,
} from 'react'
import ToggleView from './ToggleView'

interface ToggleProps {
  header: (collapsed: boolean) => React.ReactElement | null;
  content: (collapsed: boolean) => React.ReactElement | null;
  isCollapsedByDefault?: boolean;
  small?: boolean;
  toggleHandler?: (collapsed: boolean) => void
}

const Toggle = (props: ToggleProps) => {
    const {
        header,
        content,
        isCollapsedByDefault = true,
        small = false,
        toggleHandler,
    } = props

    const contentEl: Ref<HTMLDivElement> = useRef(null)
    const [collapsed, setCollapsed] = useState<boolean>(isCollapsedByDefault)

    useEffect(() => {
        if (!collapsed && contentEl.current) {
            // to prevent transition execution when component is rerendered by parent
            contentEl.current.style.setProperty('transition', 'none')
            contentEl.current.style.height = 'auto'
            contentEl.current.style.visibility = 'visible'

            // without it for dynamic content like Table initial height was not calculated correctly
            setTimeout(() => {
                if (contentEl.current) {
                    contentEl.current.style.height = `${contentEl.current.scrollHeight}px`
                }
            }, 100)
        }

        const hideContent = () => {
            if (contentEl.current && collapsed) {
                contentEl.current.style.visibility = 'hidden'
            }
        }

        if (contentEl.current) {
            contentEl.current.addEventListener('animationend', hideContent)
        }

        return () => {
            if (contentEl.current) {
                contentEl.current.removeEventListener('animationend', hideContent)
            }
        }
    }, [])

    const toggleContent = () => {
        if (!contentEl.current) {
            return
        }

        // restore transition that may be disabled in useEffect
        contentEl.current.style.setProperty('transition', 'height 0.2s ease-out')

        if (collapsed) {
            contentEl.current.style.visibility = 'visible'
            contentEl.current.style.height = `${contentEl.current.scrollHeight}px`
        } else {
            contentEl.current.style.height = ''
        }

        setCollapsed(!collapsed)
        // eslint-disable-next-line no-unused-expressions
        toggleHandler && toggleHandler(!collapsed)
    }

    return (
        <ToggleView
            collapsed={collapsed}
            content={content}
            header={header}
            ref={contentEl}
            small={small}
            toggleContent={toggleContent}
        />
    )
}

Toggle.defaultProps = {
    isCollapsedByDefault: true,
    small: false,
    toggleHandler: () => null,
}

export default Toggle
